import * as Color from "../Color";
import { ButtonAppearance } from "../Button";
import { TagType } from "../Tag";

/**
 * Defines the icon color values for the close button in normal and hover states.
 */
type CloseButtonColorState = Readonly<{
  normal: Color.RgbColor;
  hover: Color.RgbColor;
}>;

/**
 * Defines a CSS custom property and its fallback color value.
 */
type TokenColorVar = Readonly<{
  propertyName: string;
  fallback: Color.RgbColor;
}>;

/**
 * Groups token color variables for regular and optional inverted badge variants.
 */
type CloseButtonTokenColorConfig = Readonly<{
  normal: TokenColorVar;
  hover: TokenColorVar;
  inverted?: TokenColorVar;
  invertedHover?: TokenColorVar;
}>;

/**
 * Creates a token color variable descriptor with a parsed fallback color.
 */
function createTokenColorVar(propertyName: string, fallbackHex: string): TokenColorVar {
  return {
    propertyName,
    fallback: Color.fromString(fallbackHex),
  };
}

/**
 * Maps each badge type to token CSS variables and fallback colors for close button icon states.
 */
const closeButtonTokenConfigByType: Record<TagType, CloseButtonTokenColorConfig> = {
  info: {
    normal: createTokenColorVar("--badge-type-info-color-close_icon_fill", "#3e5d6f"),
    hover: createTokenColorVar("--badge-type-info-color-close_icon_fill_hover", "#56819a"),
    inverted: createTokenColorVar("--badge-type-info-inverted-close_icon_fill", "#2f4653"),
    invertedHover: createTokenColorVar("--badge-type-info-inverted-close_icon_fill", "#4a6f84"),
  },
  "info-high-contrast": {
    normal: createTokenColorVar("--badge-type-info-high_contrast-close_icon_fill", "#ffffff"),
    hover: createTokenColorVar("--badge-type-info-high_contrast-close_icon_fill", "#ffffff"),
    inverted: createTokenColorVar("--badge-type-info-high_contrast-inverted-close_icon_fill", "#3e5d6f"),
    invertedHover: createTokenColorVar("--badge-type-info-high_contrast-inverted-close_icon_fill", "#56819a"),
  },
  system: {
    normal: createTokenColorVar("--badge-type-system-color-close_icon_fill", "#00629c"),
    hover: createTokenColorVar("--badge-type-system-color-close_icon_fill_hover", "#008ee3"),
  },
  "system-high-contrast": {
    normal: createTokenColorVar("--badge-type-system-high_contrast-close_icon_fill", "#ffffff"),
    hover: createTokenColorVar("--badge-type-system-high_contrast-close_icon_fill", "#ffffff"),
    inverted: createTokenColorVar("--badge-type-system-high_contrast-inverted-close_icon_fill", "#00629c"),
    invertedHover: createTokenColorVar("--badge-type-system-high_contrast-inverted-close_icon_fill", "#008ee3"),
  },
  "system-alternative": {
    normal: createTokenColorVar("--badge-type-system_alternative-color-close_icon_fill", "#4f5b97"),
    hover: createTokenColorVar("--badge-type-system_alternative-color-close_icon_fill_hover", "#6c78b2"),
  },
  "system-alternative-high-contrast": {
    normal: createTokenColorVar("--badge-type-system_alternative-high_contrast-close_icon_fill", "#ffffff"),
    hover: createTokenColorVar("--badge-type-system_alternative-high_contrast-close_icon_fill", "#ffffff"),
    inverted: createTokenColorVar("--badge-type-system_alternative-high_contrast-inverted-close_icon_fill", "#4f5b97"),
    invertedHover: createTokenColorVar("--badge-type-system_alternative-high_contrast-inverted-close_icon_fill", "#6c78b2"),
  },
  error: {
    normal: createTokenColorVar("--badge-type-error-color-close_icon_fill", "#b8222a"),
    hover: createTokenColorVar("--badge-type-error-color-close_icon_fill_hover", "#db3a42"),
  },
  "error-high-contrast": {
    normal: createTokenColorVar("--badge-type-error-high_contrast-close_icon_fill", "#ffffff"),
    hover: createTokenColorVar("--badge-type-error-high_contrast-close_icon_fill", "#ffffff"),
    inverted: createTokenColorVar("--badge-type-error-high_contrast-inverted-close_icon_fill", "#b8222a"),
    invertedHover: createTokenColorVar("--badge-type-error-high_contrast-inverted-close_icon_fill", "#db3a42"),
  },
  warning: {
    normal: createTokenColorVar("--badge-type-warning-color-close_icon_fill", "#976400"),
    hover: createTokenColorVar("--badge-type-warning-color-close_icon_fill_hover", "#df9400"),
  },
  "warning-high-contrast": {
    normal: createTokenColorVar("--badge-type-warning-high_contrast-close_icon_fill", "#2f4653"),
    hover: createTokenColorVar("--badge-type-warning-high_contrast-close_icon_fill", "#4a6f84"),
    inverted: createTokenColorVar("--badge-type-warning-high_contrast-inverted-close_icon_fill", "#976400"),
    invertedHover: createTokenColorVar("--badge-type-warning-high_contrast-inverted-close_icon_fill", "#df9400"),
  },
  success: {
    normal: createTokenColorVar("--badge-type-success-color-close_icon_fill", "#317861"),
    hover: createTokenColorVar("--badge-type-success-color-close_icon_fill_hover", "#45a888"),
  },
  "success-high-contrast": {
    normal: createTokenColorVar("--badge-type-success-high_contrast-close_icon_fill", "#ffffff"),
    hover: createTokenColorVar("--badge-type-success-high_contrast-close_icon_fill", "#ffffff"),
    inverted: createTokenColorVar("--badge-type-success-high_contrast-inverted-close_icon_fill", "#317861"),
    invertedHover: createTokenColorVar("--badge-type-success-high_contrast-inverted-close_icon_fill", "#45a888"),
  },
};

/**
 * Parses a CSS color string into an RGB color object.
 */
function parseCssColor(colorValue?: string): Color.RgbColor | undefined {
  if (!colorValue) return undefined;
  try {
    return Color.fromString(colorValue);
  } catch {
    return undefined;
  }
}

/**
 * Reads a CSS custom property from the document root.
 */
function readCssCustomProperty(propertyName: string): string | undefined {
  if (typeof window === "undefined" || typeof document === "undefined") return undefined;
  const value = window.getComputedStyle(document.documentElement).getPropertyValue(propertyName).trim();
  return value.length > 0 ? value : undefined;
}

/**
 * Resolves a token color from CSS and falls back to its configured static color.
 */
function resolveTokenColor(tokenColorVar: TokenColorVar): Color.RgbColor {
  const tokenValue = readCssCustomProperty(tokenColorVar.propertyName);
  return parseCssColor(tokenValue) ?? tokenColorVar.fallback;
}

/**
 * Resolves close button normal and hover colors for the given badge type and variant.
 */
function resolveTokenCloseButtonColorState(
  type: TagType,
  inverted: boolean,
): CloseButtonColorState {
  const colorConfig = closeButtonTokenConfigByType[type];
  const normalToken = inverted ? colorConfig.inverted ?? colorConfig.normal : colorConfig.normal;
  const hoverToken = inverted
    ? colorConfig.invertedHover ?? colorConfig.inverted ?? colorConfig.hover
    : colorConfig.hover;

  const normal = resolveTokenColor(normalToken);
  const hover = resolveTokenColor(hoverToken);

  return { normal, hover };
}

/**
 * Builds ButtonAppearance for the badge close button.
 */
export function getBadgeCloseButtonAppearance(type: TagType, inverted: boolean): ButtonAppearance {
  const selectedColor = resolveTokenCloseButtonColorState(type, inverted);

  return {
    padding: { horizontal: 0, vertical: 0 },
    label: {
      letterSpacing: 0,
      uppercase: false,
      weight: "regular",
      fontSize: 16,
      lineHeight: 16,
    },
    icon: {
      size: "regular",
    },
    normal: {
      backgroundColor: Color.Transparent,
      foregroundColor: selectedColor.normal,
      borderColor: Color.Transparent,
    },
    hover: {
      backgroundColor: Color.Transparent,
      foregroundColor: selectedColor.hover,
      borderColor: Color.Transparent,
    },
    pressed: {
      backgroundColor: Color.Transparent,
      foregroundColor: selectedColor.hover,
      borderColor: Color.Transparent,
    },
    disabled: {
      backgroundColor: Color.Transparent,
      foregroundColor: selectedColor.normal,
      borderColor: Color.Transparent,
    },
  };
}
